import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../providers/AuthProvider';
import { 
  ArrowLeft, Store, Coins, Tag, ShoppingCart, 
  Filter, AlertCircle, X, Percent,
  RefreshCw, PackageOpen, Clock, Trash2, ListOrdered
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EnhancedCard from '../components/EnhancedCard';
import { getInventory } from '../utils/inventoryManager';
import { getCardImageById } from '../utils/cardImageHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rarityColors = {
  common: '#A0A0A0',
  rare: '#00CCFF',
  epic: '#FF00FF',
  legendary: '#FFD700',
};

// Auction duration options
const AUCTION_DURATIONS = [
  { value: 1, label: '1 Hour' },
  { value: 6, label: '6 Hours' },
  { value: 12, label: '12 Hours' },
  { value: 24, label: '24 Hours' },
];

// LocalStorage key for my listings
const MY_LISTINGS_KEY = 'my_marketplace_listings';

// Save my listings to localStorage
const saveMyListings = (listings) => {
  try {
    localStorage.setItem(MY_LISTINGS_KEY, JSON.stringify(listings));
  } catch (e) {
    console.error('Failed to save listings:', e);
  }
};

// Load my listings from localStorage
const loadMyListings = () => {
  try {
    const data = localStorage.getItem(MY_LISTINGS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

// List Card Modal with Auction Duration
const ListCardModal = ({ card, onClose, onList, walletAddress }) => {
  const [price, setPrice] = useState(100);
  const [currency, setCurrency] = useState('slap');
  const [duration, setDuration] = useState(24); // Default 24 hours
  const [isListing, setIsListing] = useState(false);

  const creatorFee = price * 0.05;
  const sellerReceives = price - creatorFee;

  // Get card image
  const cardImage = getCardImageById(card);

  const handleList = async () => {
    if (price <= 0) {
      toast.error('Price must be positive');
      return;
    }

    setIsListing(true);
    try {
      // Create listing object
      const listing = {
        id: card.id,
        card_id: card.id,
        character_name: card.character_name,
        title: card.title,
        rarity: card.rarity,
        power_rating: card.power_rating,
        card_class: card.card_class,
        price: price,
        price_currency: currency,
        duration_hours: duration,
        listed_at: Date.now(),
        expires_at: Date.now() + (duration * 60 * 60 * 1000),
        seller_id: walletAddress,
        owner_id: walletAddress,
      };

      // Try to list on server
      try {
        await axios.post(`${API}/marketplace/v2/list`, {
          card_id: card.id,
          price: price,
          currency: currency,
          seller_id: walletAddress,
          duration_hours: duration,
        });
      } catch (e) {
        console.log('Server listing failed, using local only');
      }

      // Save to localStorage for persistence
      const myListings = loadMyListings();
      myListings.push(listing);
      saveMyListings(myListings);

      toast.success('Card listed successfully!', {
        description: `Listed for ${duration} hours at ${price} ${currency.toUpperCase()}`
      });
      onList(listing);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to list card');
    } finally {
      setIsListing(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[900] flex items-center justify-center bg-black/90 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-[#0a0a0a] border-2 border-neon-pink rounded-2xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors"
          data-testid="list-modal-close-x"
        >
          <X className="w-5 h-5 text-red-400" />
        </button>

        <div className="flex justify-between items-start mb-4 pr-10">
          <h2 className="font-anton text-2xl text-white flex items-center gap-2">
            <Tag className="w-6 h-6 text-neon-pink" />
            LIST FOR SALE
          </h2>
        </div>

        {/* Card Preview */}
        <div className="flex gap-4 mb-6">
          <div 
            className="w-28 h-36 rounded-lg overflow-hidden border-2 flex-shrink-0" 
            style={{ borderColor: rarityColors[card.rarity] }}
          >
            <img
              src={cardImage}
              alt={card.character_name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h3 className="font-anton text-lg text-white">{card.character_name}</h3>
            <p className="font-bebas text-sm text-white/60">{card.title}</p>
            <p className="font-space text-xs uppercase mt-2" style={{ color: rarityColors[card.rarity] }}>
              {card.rarity}
            </p>
            <div className="flex items-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < card.power_rating ? 'text-yellow-400' : 'text-gray-600'}>★</span>
              ))}
            </div>
          </div>
        </div>

        {/* AUCTION DURATION - NEW */}
        <div className="mb-4">
          <label className="font-space text-sm text-white/60 block mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            AUCTION DURATION
          </label>
          <div className="grid grid-cols-4 gap-2">
            {AUCTION_DURATIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDuration(opt.value)}
                className={`py-2 px-2 rounded-lg border-2 text-xs font-bold transition-all ${
                  duration === opt.value 
                    ? 'border-green-500 bg-green-500/20 text-green-400' 
                    : 'border-white/20 text-white/60 hover:border-white/40'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Currency Selection */}
        <div className="mb-4">
          <label className="font-space text-sm text-white/60 block mb-2">CURRENCY</label>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrency('slap')}
              className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                currency === 'slap' 
                  ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400' 
                  : 'border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <Coins className="w-4 h-4" />
              $SLAP
            </button>
            <button
              onClick={() => setCurrency('sol')}
              className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                currency === 'sol' 
                  ? 'border-purple-500 bg-purple-500/20 text-purple-400' 
                  : 'border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <span className="text-lg">◎</span>
              SOL
            </button>
          </div>
        </div>

        {/* Price Input */}
        <div className="mb-4">
          <label className="font-space text-sm text-white/60 block mb-2">SET YOUR PRICE</label>
          <div className="relative">
            {currency === 'slap' ? (
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400" />
            ) : (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 text-lg">◎</span>
            )}
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(Math.max(0, parseFloat(e.target.value) || 0))}
              step={currency === 'sol' ? '0.01' : '1'}
              className="w-full bg-black/40 border-2 border-white/20 rounded-lg pl-10 pr-4 py-3 font-anton text-xl text-white focus:border-neon-pink outline-none"
            />
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="bg-white/5 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="font-space text-sm text-white/60 flex items-center gap-1">
              <Percent className="w-4 h-4" />
              Creator Fee (5%)
            </span>
            <span className="font-anton text-red-400">
              -{creatorFee.toFixed(currency === 'sol' ? 4 : 0)} {currency.toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-white/10">
            <span className="font-space text-sm text-white/60">You Receive</span>
            <span className="font-anton text-lg text-neon-green">
              {sellerReceives.toFixed(currency === 'sol' ? 4 : 0)} {currency.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={onClose}
            className="flex-1 bg-transparent border-2 border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleList}
            disabled={isListing || price <= 0}
            className="flex-1 bg-neon-pink hover:bg-neon-pink/80 text-white font-anton"
          >
            {isListing ? 'Listing...' : 'LIST CARD'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Card Image Component with fallback
const CardImage = ({ card }) => {
  const imageSrc = getCardImageById(card);
  
  return (
    <div 
      className="w-full h-full rounded-lg overflow-hidden"
      style={{ 
        borderColor: rarityColors[card.rarity],
        background: `linear-gradient(135deg, ${rarityColors[card.rarity]}20, transparent)`
      }}
    >
      <img
        src={imageSrc}
        alt={card.character_name || 'Card'}
        className="w-full h-full object-cover"
        onError={(e) => {
          // Fallback to generated image
          e.target.src = getCardImageById({ ...card, image_base64: null });
        }}
      />
    </div>
  );
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user, walletAddress, isAuthenticated, refreshUser } = useAuth();
  
  const [listings, setListings] = useState([]);
  const [myCards, setMyCards] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse'); // 'browse', 'sell', 'my_listings'
  const [selectedCard, setSelectedCard] = useState(null);
  const [filterRarity, setFilterRarity] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [sortBy, setSortBy] = useState('price_low');
  const [buying, setBuying] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch public listings
  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/marketplace`);
      setListings(response.data.cards || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch my cards (available to sell)
  const fetchMyCards = useCallback(async () => {
    if (!walletAddress) return;
    setLoading(true);
    try {
      const localInventory = getInventory();
      
      // Filter out cards that are already listed
      const listedIds = new Set(myListings.map(l => l.card_id));
      const availableCards = localInventory.filter(c => !listedIds.has(c.id));
      
      // Try to also get server cards
      try {
        const response = await axios.get(`${API}/cards/collection?session_id=${walletAddress}&_t=${Date.now()}`);
        let serverCards = (response.data.cards || []).filter(c => !c.is_listed && !listedIds.has(c.id));
        
        const localCardIds = new Set(availableCards.map(c => c.id));
        const uniqueServerCards = serverCards.filter(c => c.id && !localCardIds.has(c.id));
        
        setMyCards([...availableCards, ...uniqueServerCards]);
      } catch (error) {
        setMyCards(availableCards);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
      setMyCards([]);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, myListings]);

  // Load my listings from localStorage
  const loadMyListingsFromStorage = useCallback(() => {
    const stored = loadMyListings();
    // Filter to only show listings from current wallet and not expired
    const now = Date.now();
    const validListings = stored.filter(l => 
      l.seller_id === walletAddress && 
      l.expires_at > now
    );
    
    // Clean up expired listings
    if (validListings.length !== stored.length) {
      saveMyListings(validListings);
    }
    
    setMyListings(validListings);
  }, [walletAddress]);

  // Initial data load
  useEffect(() => {
    fetchListings();
    loadMyListingsFromStorage();
  }, [loadMyListingsFromStorage]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMyCards();
    }
  }, [isAuthenticated, walletAddress, fetchMyCards]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'sell') {
        await fetchMyCards();
        if (refreshUser) await refreshUser();
      } else if (activeTab === 'my_listings') {
        loadMyListingsFromStorage();
      } else {
        await fetchListings();
      }
      toast.success('Refreshed!');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Listen for inventory updates
  useEffect(() => {
    const handleInventoryUpdate = () => {
      if (isAuthenticated) {
        fetchMyCards();
      }
    };
    
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    return () => window.removeEventListener('inventory-updated', handleInventoryUpdate);
  }, [isAuthenticated, fetchMyCards]);

  // Buy handler
  const handleBuy = async (card) => {
    if (!isAuthenticated) {
      toast.error('Connect your wallet to buy');
      return;
    }

    const currency = card.price_currency || 'slap';
    const price = card.price || 0;
    
    if (currency === 'slap') {
      const userBalance = user?.slap_tokens || user?.coins || 0;
      if (userBalance < price) {
        toast.error('Not enough $SLAP tokens!');
        return;
      }
    }

    setBuying(card.id);
    try {
      await axios.post(`${API}/marketplace/v2/buy?card_id=${card.id}&buyer_session=${walletAddress}`);
      toast.success(`You bought ${card.character_name}!`, {
        description: `Paid ${price} ${currency.toUpperCase()}`
      });
      await fetchListings();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to buy card');
    } finally {
      setBuying(null);
    }
  };

  // Cancel my listing
  const handleCancelListing = async (listing) => {
    try {
      // Try server first
      try {
        await axios.post(`${API}/marketplace/unlist?card_id=${listing.card_id}&session_id=${walletAddress}`);
      } catch (e) {
        console.log('Server unlist failed, removing locally');
      }

      // Remove from localStorage
      const updated = myListings.filter(l => l.id !== listing.id);
      setMyListings(updated);
      saveMyListings(updated);
      
      toast.success('Listing cancelled', {
        description: `${listing.character_name} returned to your inventory`
      });
      
      // Refresh inventory
      fetchMyCards();
    } catch (error) {
      toast.error('Failed to cancel listing');
    }
  };

  // Handle new listing
  const handleListSuccess = (listing) => {
    setMyListings(prev => [...prev, listing]);
    fetchMyCards();
    fetchListings();
  };

  // Filter and sort listings
  const filteredListings = listings
    .filter(card => filterRarity === 'all' || card.rarity === filterRarity)
    .filter(card => filterCurrency === 'all' || (card.price_currency || 'slap') === filterCurrency)
    .sort((a, b) => {
      if (sortBy === 'price_low') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price_high') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'rarity') {
        const order = { legendary: 0, epic: 1, rare: 2, common: 3 };
        return order[a.rarity] - order[b.rarity];
      }
      return 0;
    });

  const slapTokens = user?.slap_tokens || user?.coins || 0;

  // Time remaining helper
  const getTimeRemaining = (expiresAt) => {
    const now = Date.now();
    const diff = expiresAt - now;
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-24">
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FFD70015 1px, transparent 1px),
            linear-gradient(90deg, #FFD70015 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] bg-yellow-500/10 rounded-full blur-[150px] pointer-events-none" />

      {/* List Card Modal */}
      <AnimatePresence>
        {selectedCard && (
          <ListCardModal
            card={selectedCard}
            onClose={() => setSelectedCard(null)}
            onList={handleListSuccess}
            walletAddress={walletAddress}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate('/')}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            {isAuthenticated && (
              <div className="flex gap-3">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 border border-yellow-500/50">
                  <Coins className="w-4 h-4 text-yellow-400" />
                  <span className="font-anton text-lg text-yellow-400">{slapTokens.toLocaleString()}</span>
                  <span className="font-space text-xs text-white/40">$SLAP</span>
                </div>
              </div>
            )}
          </div>

          <motion.h1
            className="font-anton text-4xl md:text-6xl uppercase tracking-tight text-center"
            style={{
              background: 'linear-gradient(180deg, #FFD700 0%, #FF6600 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Store className="inline w-10 h-10 mr-3" />
            P2P MARKETPLACE
          </motion.h1>
          <p className="font-space text-sm text-white/60 text-center mt-3">
            TRADE CARDS • SET YOUR PRICE • 5% CREATOR FEE
          </p>
        </div>
      </header>

      {/* Tabs - 3 TABS: Browse, Sell Cards, My Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex justify-center gap-2 sm:gap-4 mb-6 flex-wrap">
          {[
            { id: 'browse', label: 'Browse', icon: ShoppingCart },
            { id: 'sell', label: 'Sell Cards', icon: Tag },
            { id: 'my_listings', label: 'My Listings', icon: ListOrdered, badge: myListings.length },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-anton text-sm sm:text-lg uppercase px-4 sm:px-8 py-3 relative ${
                  activeTab === tab.id
                    ? 'bg-yellow-500 text-black shadow-[0_0_30px_rgba(255,215,0,0.5)]'
                    : 'bg-black/40 border-2 border-white/20 text-white/60'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.id === 'my_listings' ? 'My' : tab.label.split(' ')[0]}</span>
                {tab.badge > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6 justify-center">
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <Filter className="w-4 h-4 text-white/60" />
                {['all', 'common', 'rare', 'epic', 'legendary'].map((rarity) => (
                  <button
                    key={rarity}
                    onClick={() => setFilterRarity(rarity)}
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase transition-all ${
                      filterRarity === rarity
                        ? 'bg-white text-black'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                    style={filterRarity === rarity && rarity !== 'all' ? { backgroundColor: rarityColors[rarity], color: '#000' } : {}}
                  >
                    {rarity}
                  </button>
                ))}
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-black/40 border border-white/20 rounded-lg px-4 py-2 text-white font-space text-sm"
              >
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="rarity">Rarity</option>
              </select>
            </div>

            {/* Listings Grid */}
            {filteredListings.length === 0 ? (
              <div className="text-center py-16">
                <Store className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="font-anton text-2xl text-white/40">NO LISTINGS YET</p>
                <p className="font-space text-sm text-white/30 mt-2">Be the first to list a card!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                {filteredListings.map((card) => {
                  const currency = card.price_currency || 'slap';
                  const isOwner = card.owner_id === walletAddress;
                  
                  return (
                    <motion.div
                      key={card.id}
                      className="bg-black/40 border-2 border-white/10 rounded-xl p-3 sm:p-4 hover:border-yellow-500/50 transition-all"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="aspect-[3/4] mb-3">
                        <EnhancedCard card={card} showNews={false} />
                      </div>
                      
                      <div className="flex items-center justify-between mb-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-anton text-sm sm:text-base text-white truncate">{card.character_name}</p>
                          <p className="font-space text-xs" style={{ color: rarityColors[card.rarity] }}>{card.rarity}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <div className="flex items-center gap-1 justify-end">
                            {currency === 'slap' ? (
                              <Coins className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                            ) : (
                              <span className="text-purple-400 text-sm">◎</span>
                            )}
                            <span className={`font-anton text-base sm:text-xl ${currency === 'slap' ? 'text-yellow-400' : 'text-purple-400'}`}>
                              {card.price}
                            </span>
                          </div>
                        </div>
                      </div>

                      {isOwner ? (
                        <Button
                          onClick={() => handleCancelListing(card)}
                          className="w-full bg-red-600/20 border border-red-500 text-red-400 hover:bg-red-600/40 font-anton text-xs sm:text-sm"
                        >
                          UNLIST
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleBuy(card)}
                          disabled={buying === card.id}
                          className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-anton text-xs sm:text-sm"
                        >
                          {buying === card.id ? 'BUYING...' : 'BUY NOW'}
                        </Button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Sell Tab */}
        {activeTab === 'sell' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {!isAuthenticated ? (
              <div className="text-center py-16">
                <AlertCircle className="w-16 h-16 text-neon-pink mx-auto mb-4" />
                <p className="font-anton text-2xl text-white">CONNECT YOUR WALLET</p>
                <p className="font-space text-sm text-white/60 mt-2">to list your cards for sale</p>
              </div>
            ) : (
              <>
                {/* Refresh Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-white/60 text-sm">
                    Available: <span className="text-yellow-400 font-bold text-lg">{myCards.length}</span> cards
                  </p>
                  <Button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-500 text-white border-0 font-bold py-3 px-6"
                    data-testid="reload-my-cards-btn"
                  >
                    <RefreshCw className={`w-5 h-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'LOADING...' : 'REFRESH'}
                  </Button>
                </div>

                {myCards.length === 0 ? (
                  <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
                    <PackageOpen className="w-20 h-20 text-white/20 mx-auto mb-4" />
                    <p className="font-anton text-2xl text-white/60 mb-2">NO CARDS TO SELL</p>
                    <p className="font-space text-sm text-white/40 mb-6">
                      Open a Mystery Box first to get cards!
                    </p>
                    <Button
                      onClick={() => navigate('/')}
                      className="bg-neon-pink hover:bg-neon-pink/80 text-white"
                    >
                      <PackageOpen className="w-4 h-4 mr-2" />
                      OPEN MYSTERY BOX
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {myCards.map((card) => (
                      <motion.div
                        key={card.id}
                        className="bg-black/40 border-2 border-white/10 rounded-xl p-2 sm:p-4 hover:border-neon-pink/50 transition-all cursor-pointer"
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setSelectedCard(card)}
                      >
                        <div className="aspect-[3/4] mb-2 sm:mb-3 rounded-lg overflow-hidden border-2" style={{ borderColor: rarityColors[card.rarity] }}>
                          <CardImage card={card} />
                        </div>
                        <p className="font-anton text-xs sm:text-sm text-white truncate mb-2">{card.character_name}</p>
                        <Button className="w-full bg-neon-pink hover:bg-neon-pink/80 text-white font-anton text-xs sm:text-sm py-2">
                          <Tag className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          SELL
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* MY LISTINGS Tab - NEW */}
        {activeTab === 'my_listings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {!isAuthenticated ? (
              <div className="text-center py-16">
                <AlertCircle className="w-16 h-16 text-neon-pink mx-auto mb-4" />
                <p className="font-anton text-2xl text-white">CONNECT YOUR WALLET</p>
                <p className="font-space text-sm text-white/60 mt-2">to view your listings</p>
              </div>
            ) : myListings.length === 0 ? (
              <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
                <ListOrdered className="w-20 h-20 text-white/20 mx-auto mb-4" />
                <p className="font-anton text-2xl text-white/60 mb-2">NO ACTIVE LISTINGS</p>
                <p className="font-space text-sm text-white/40 mb-6">
                  List a card from your inventory to start selling!
                </p>
                <Button
                  onClick={() => setActiveTab('sell')}
                  className="bg-neon-pink hover:bg-neon-pink/80 text-white"
                >
                  <Tag className="w-4 h-4 mr-2" />
                  GO TO SELL TAB
                </Button>
              </div>
            ) : (
              <>
                {/* Info Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                  <p className="text-white/80 text-sm">
                    You have <span className="text-yellow-400 font-bold text-lg">{myListings.length}</span> active listing{myListings.length !== 1 ? 's' : ''}
                  </p>
                  <Button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    REFRESH
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myListings.map((listing) => {
                    const currency = listing.price_currency || 'slap';
                    const timeLeft = getTimeRemaining(listing.expires_at);
                    const isExpired = timeLeft === 'Expired';
                    
                    return (
                      <motion.div
                        key={listing.id}
                        className={`bg-black/40 border-2 rounded-xl p-4 ${isExpired ? 'border-red-500/50 opacity-60' : 'border-white/10'}`}
                        whileHover={{ scale: 1.01 }}
                      >
                        <div className="flex gap-4">
                          {/* Card Preview */}
                          <div 
                            className="w-20 h-28 rounded-lg overflow-hidden border-2 flex-shrink-0" 
                            style={{ borderColor: rarityColors[listing.rarity] }}
                          >
                            <CardImage card={listing} />
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-anton text-base text-white truncate">{listing.character_name}</p>
                            <p className="font-space text-xs" style={{ color: rarityColors[listing.rarity] }}>
                              {listing.rarity}
                            </p>
                            
                            {/* Price */}
                            <div className="flex items-center gap-1 mt-2">
                              {currency === 'slap' ? (
                                <Coins className="w-4 h-4 text-yellow-400" />
                              ) : (
                                <span className="text-purple-400">◎</span>
                              )}
                              <span className={`font-anton text-lg ${currency === 'slap' ? 'text-yellow-400' : 'text-purple-400'}`}>
                                {listing.price}
                              </span>
                              <span className="text-white/40 text-xs ml-1">{currency.toUpperCase()}</span>
                            </div>
                            
                            {/* Time Remaining */}
                            <div className={`flex items-center gap-1 mt-1 text-xs ${isExpired ? 'text-red-400' : 'text-white/60'}`}>
                              <Clock className="w-3 h-3" />
                              {timeLeft}
                            </div>
                          </div>
                        </div>
                        
                        {/* Cancel Button */}
                        <Button
                          onClick={() => handleCancelListing(listing)}
                          className="w-full mt-4 bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/40 font-anton text-sm"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          CANCEL LISTING
                        </Button>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MarketplacePage;
